import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { SITE_NAME, SITE_URL } from "../lib/api";
import { LANGS } from "../i18n";
import { localizePath, lpFull } from "../lib/urls";

const DEFAULT_IMAGE = `${SITE_URL}/og-image.jpg`;
const OG_LOCALES = { en: "en_US", es: "es_ES", it: "it_IT", de: "de_DE", pt: "pt_BR", fr: "fr_FR", nl: "nl_NL", ru: "ru_RU", lt: "lt_LT" };

export const SEO = ({ title, description, path = "", jsonLd = [], type = "website", image, noAlternates = false, altLangs = null, noindex = false }) => {
  const { i18n } = useTranslation();
  const curLang = (i18n.language || "en").slice(0, 2);
  const altList = altLangs ? LANGS.filter((l) => l.code === "en" || altLangs.includes(l.code)) : LANGS;
  const fullTitle = title
    ? `${title} | ${title.length > 46 ? "VANROCK" : SITE_NAME}`
    : `${SITE_NAME} — Undercarriage Parts for Excavators, Bulldozers & Loaders`;
  const langUrl = (lg) => `${SITE_URL}/${lg}${localizePath(path, lg) === "/" && !path ? "" : localizePath(path || "/", lg)}`.replace(/\/$/, "") || `${SITE_URL}/${lg}`;
  const enUrl = langUrl("en");
  const localized = !noAlternates && altList.some((l) => l.code === curLang);
  const canonical = localized && curLang !== "en" ? langUrl(curLang) : enUrl;
  const ogImage = image || DEFAULT_IMAGE;
  return (
    <Helmet htmlAttributes={{ lang: curLang }}>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <meta name="robots" content={noindex ? "noindex, follow" : "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"} />
      <link rel="canonical" href={canonical} />
      {!noAlternates && <link rel="alternate" hrefLang="x-default" href={enUrl} />}
      {!noAlternates && altList.map((l) => (
        <link key={l.code} rel="alternate" hrefLang={l.code} href={langUrl(l.code)} />
      ))}
      <meta property="og:locale" content={OG_LOCALES[curLang] || "en_US"} />
      {!noAlternates && altList.filter((l) => l.code !== curLang).map((l) => (
        <meta key={`ogl-${l.code}`} property="og:locale:alternate" content={OG_LOCALES[l.code]} />
      ))}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={ogImage} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
      {jsonLd.map((obj, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(obj)}
        </script>
      ))}
    </Helmet>
  );
};

export const breadcrumbJsonLd = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((it, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: it.name,
    ...(it.path ? { item: `${SITE_URL}${lpFull(it.path)}` } : {}),
  })),
});

export const faqJsonLd = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
});

export const itemListJsonLd = (name, items) => ({
  "@context": "https://schema.org",
  "@type": "ItemList",
  name,
  numberOfItems: items.length,
  itemListElement: items.map((it, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: it.name,
    url: `${SITE_URL}${lpFull(it.path || "/")}`,
  })),
});
